/**
 * «منشی هوشمند» in the doctor panel (PM-0517) — the document, the vocabulary and
 * the pure rules of the record button. DOM-free: the hooks and the screens import
 * from here, never the reverse.
 *
 * The secretary PROPOSES and the doctor COMMITS: `RUN_SECRETARY_COMMAND` writes
 * nothing, and a booking is made by the existing «ثبت نوبت» form, opened filled in.
 */
import { errorCode } from '../hooks/useDoctorBooking';

const PATIENT = 'id name fileNumber age phoneTail isNew score';

export const RUN_SECRETARY_COMMAND = `
  mutation RunSecretaryCommand($input: SecretaryCommandInput!) {
    runSecretaryCommand(input: $input) {
      outcome
      kind
      charged
      heardName
      detail
      reason
      topic
      patients { ${PATIENT} }
      patient { ${PATIENT} }
      booking {
        patient { ${PATIENT} }
        clinic { clinicId name canBookDirectly }
        clinicAssumed
        fromMs
        toMs
        slots { dateTime clinicId clinicName }
        requestedAt
        requestedFree
        note
        newPatientPhone
      }
      daySummary {
        dateMs total scheduled requested arrived inVisit completed cancelled noShow
        next { appointmentId patientId patientName dateTime status clinicName }
      }
    }
  }
`;

/** The price list's key for one planning step, and the transcription minute's client action. */
export const PRICE_SERVICE = 'PlanClinicCommand';
export const PRICE_TRANSCRIPTION = 'TRANSCRIPTION_PARSINA_PER_MIN';

export const OUT_OF_SCOPE_TOPICS = Object.freeze([
  'clinical_record', 'visit_flow', 'mark_arrived', 'reschedule_or_cancel', 'money', 'send_message', 'delete_record', 'other',
]);

/** Day-summary statuses the window has words for. */
export const UPCOMING_STATUSES = Object.freeze(['scheduled', 'requested', 'arrived', 'in_visit']);

/** How the BFF chose the clinic, when it chose — each has a line in the form's notice. */
export const CLINIC_ASSUMPTIONS = Object.freeze(['reception_clinic', 'working_hours', 'earliest_free']);

const CREDIT_CODES = new Set(['INSUFFICIENT_CREDITS', 'INSUFFICIENT_CREDIT']);

/** Is this failure «پارسه کافی نیست»? The one failure with a fix the doctor owns. */
export function isCreditRefusal(err) {
  return CREDIT_CODES.has(errorCode(err));
}

/**
 * The least a spoken command can cost: one started minute of transcription plus the
 * command itself. Null when either price is unknown — the server then decides.
 */
export function minimumCost(minute, command) {
  return Number.isFinite(minute) && Number.isFinite(command) ? minute + command : null;
}

/** A known balance below the known minimum. Anything unknown is NOT a refusal. */
export function cannotAfford(balance, minute, command) {
  const min = minimumCost(minute, command);
  return Number.isFinite(balance) && min !== null && balance < min;
}

/** The patient a result is about, whichever payload carries them. */
export function resultPatient(result) {
  return result?.booking?.patient || result?.patient || null;
}

/** Do the proposed times sit at more than one clinic? Then the doctor picks one first. */
export const slotsSpanClinics = (booking) => new Set((booking?.slots || []).map((s) => s.clinicId)).size > 1;

/**
 * Where opening a result goes:
 *   'form'     — a booking at ONE clinic: «ثبت نوبت», filled in with the first time;
 *   'formDay'  — no free time where it looked: «ثبت نوبت» on that day, the doctor picks;
 *   'navigate' — «باز کردن پرونده»: the record itself;
 *   'window'   — everything else, including times at several clinics to choose from.
 */
export function routeOf(result) {
  if (!result) return 'window';
  if (result.outcome === 'PROPOSAL' && result.booking?.slots?.length) {
    return slotsSpanClinics(result.booking) ? 'window' : 'form';
  }
  if (result.outcome === 'NO_SLOT' && result.booking) return 'formDay';
  if (result.outcome === 'NAVIGATE' && result.patient?.id) return 'navigate';
  return 'window';
}

/**
 * What the booking form needs to open filled in, for one proposed time (or, with
 * `slotIndex` -1, for the window's first day — NO_SLOT). A patient with no record
 * travels as a name: the form opens on «بیمار جدید» and registers them itself.
 */
export function bookingHandoff(booking, slotIndex = 0) {
  if (!booking) return null;
  const slot = slotIndex >= 0 ? booking.slots?.[slotIndex] || null : null;
  const isNew = !!booking.patient?.isNew;
  return {
    clinicId: slot?.clinicId || booking.clinic.clinicId,
    patient: isNew ? null : { id: booking.patient.id, name: booking.patient.name, phone: '' },
    newPatientName: isNew ? booking.patient.name : null,
    newPatientPhone: isNew ? booking.newPatientPhone || null : null,
    dateTime: slot ? slot.dateTime : null,
    dayMs: slot ? null : booking.fromMs,
    note: booking.note || null,
    clinicAssumed: CLINIC_ASSUMPTIONS.includes(booking.clinicAssumed) ? booking.clinicAssumed : null,
  };
}

/** "MM:SS" for the recording clock, ASCII — rendered through formatDigits. */
export function clockOf(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
}
